import { getMetaConsent, subscribeMetaConsent } from "./metaConsent";

export const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const scriptUrl = process.env.REACT_APP_META_PIXEL_SCRIPT_URL;
const hostConfig = process.env.REACT_APP_META_PIXEL_ALLOWED_HOSTS;
if (!META_PIXEL_ID || !scriptUrl || !hostConfig) {
  throw new Error("Meta Pixel configuration is missing.");
}
const liveHosts = new Set(hostConfig.split(",").map((host) => host.trim()));
export const isLiveMetaHost = (hostname = window.location.hostname) => liveHosts.has(hostname);

let initialized = false;
let mockInitialized = false;
let granted = false;
let script = null;
let scriptLoaded = false;
let pendingEvents = [];
const accepted = () => getMetaConsent() === "accepted";
const mock = (...args) => console.info("[Meta Pixel MOCKED]", ...args);

export function discardPendingMetaEvents() {
  const discarded = pendingEvents;
  pendingEvents = [];
  discarded.forEach(({ onDiscard }) => onDiscard?.());
}

function installStub() {
  if (window.fbq) return;
  const fbq = function (...args) {
    if (fbq.callMethod) fbq.callMethod.apply(fbq, args);
    else fbq.queue.push(args);
  };
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;
}

function flushEvents() {
  if (!accepted() || !isLiveMetaHost()) {
    revokeMetaConsent();
    return;
  }
  if (!pendingEvents.length && !initialized) return;
  if (!initialized) {
    window.fbq("set", "autoConfig", false, META_PIXEL_ID);
    window.fbq("init", META_PIXEL_ID); // Deliberately no advanced-matching data.
    initialized = true;
  }
  if (!granted) {
    window.fbq("consent", "grant");
    granted = true;
  }
  const events = pendingEvents;
  pendingEvents = [];
  for (const { event } of events) {
    if (!accepted() || !isLiveMetaHost()) break;
    window.fbq("trackSingle", META_PIXEL_ID, event);
  }
}

function loadScript() {
  if (script) return;
  installStub();
  // Router tracking is the only source of PageViews; no automatic history listener.
  window.fbq.disablePushState = true;
  window.fbq("consent", "revoke");
  script = document.createElement("script");
  script.async = true;
  script.src = scriptUrl;
  script.dataset.metaPixel = META_PIXEL_ID;
  script.onload = () => {
    scriptLoaded = true;
    try {
      // Never queue init/grant/events inside fbq while the script is in flight.
      // A withdrawal clears our queue, even if the script finishes loading later.
      flushEvents();
    } catch {
      discardPendingMetaEvents();
      console.warn("Meta Pixel unavailable; website and quote submission are unaffected.");
    }
  };
  script.onerror = () => {
    discardPendingMetaEvents();
    script?.remove();
    script = null;
    scriptLoaded = false;
    console.warn("Meta Pixel blocked or unavailable; no events replayed.");
  };
  document.head.appendChild(script);
}

export function revokeMetaConsent() {
  discardPendingMetaEvents();
  granted = false;
  try {
    if (!isLiveMetaHost()) {
      if (mockInitialized) mock("consent", "revoke");
      return;
    }
    if (typeof window.fbq === "function") {
      window.fbq.queue?.splice(0);
      window.fbq("consent", "revoke");
    }
    // A script already downloaded cannot be unloaded. Leave it revoked, and
    // guard its load callback as well as every future application event.
    for (const name of ["_fbp", "_fbc"]) {
      const expiry = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
      document.cookie = expiry;
      const labels = window.location.hostname.split(".");
      for (let i = 0; i < labels.length - 1; i += 1) {
        document.cookie = `${expiry}; domain=.${labels.slice(i).join(".")}`;
      }
    }
  } catch {
    // Tracking must never prevent a preference change or a quote submission.
  }
}

subscribeMetaConsent(() => {
  if (!accepted()) {
    revokeMetaConsent();
    return;
  }
  // Reacceptance restores consent without reinitializing or inventing a PageView.
  try {
    if (!isLiveMetaHost()) {
      if (mockInitialized && !granted) {
        mock("consent", "grant");
        granted = true;
      }
    } else if (initialized && scriptLoaded) {
      flushEvents();
    }
  } catch {
    discardPendingMetaEvents();
  }
});

export function sendMetaEvent(event, onDiscard) {
  if (!accepted() || !["PageView", "Lead"].includes(event)) return false;
  try {
    // Hostname is checked at the actual call site, never a NODE_ENV/test override.
    if (!isLiveMetaHost()) {
      if (!mockInitialized) {
        mock("set", "autoConfig", false, META_PIXEL_ID);
        mock("init", META_PIXEL_ID);
        mockInitialized = true;
      }
      if (!granted) {
        mock("consent", "grant");
        granted = true;
      }
      mock("trackSingle", META_PIXEL_ID, event);
      return true;
    }
    pendingEvents.push({ event, onDiscard });
    if (scriptLoaded) flushEvents();
    else loadScript();
    return true;
  } catch {
    discardPendingMetaEvents();
    console.warn("Meta Pixel unavailable; website and quote submission are unaffected.");
    return false;
  }
}